import { withDerivedTriggerWire } from '@/containers/playbooks/custom-builder/graph-ops';
import {
  crmLabel,
  nodeSummary,
  triggerConfigForKind,
} from '@/containers/playbooks/custom-builder/node-metadata';
import type { WebhookPreset } from '@/containers/playbooks/custom-builder/trigger-kinds';
import {
  triggerHealthNotice,
  type TriggerHealthNotice,
} from '@/containers/playbooks/custom-builder/trigger-health';
import type { CrmOption } from '@/types';
import {
  triggerKind,
  type CustomGraph,
  type PlayCrm,
  type TriggerConfig,
  type TriggerKind,
} from '@/types/custom-playbooks';
import type { AgentDto, TriggerDto, TriggerInput } from '@/types/gtm-agents';
import { offeredConnectionTypes } from './agent-tab.util';
import {
  oneTimeWords,
  scheduleWithZone,
  timeAgo,
  triggerLabel,
  type OneTimeConfig,
} from './agent-row.util';
import { BRAND } from '@/lib/brand';

/**
 * The Triggers tab's logic: the app events the engine runs, a trigger in
 * words, which kinds the tab offers, and the PATCH entry each form writes.
 * The backend keeps one trigger per play: the play's trigger card. A
 * schedule with nothing to process is a scheduled brief (W2-24): its own
 * internal play, which the Schedule form sets. An agent's own schedule or
 * one-time trigger, from before a play ran everything, is still read but
 * nothing runs it until the Schedule form saves it as a brief.
 */

export type AppEventKey =
  | 'salesforce-deal-stage'
  | 'salesforce-deal-stalled'
  | 'hubspot-deal-stage'
  | 'hubspot-deal-stalled'
  | 'attio-deal-stage'
  | 'attio-deal-stalled'
  | 'salesforce-sixsense-stage'
  | 'meeting-booked'
  | 'meeting-starting'
  | 'meeting-no-notes'
  | 'salesforce-record'
  | 'salesforce-list'
  | 'salesforce-duplicates'
  | 'hubspot-duplicates'
  | 'hubspot-record'
  | 'hubspot-list'
  | 'attio-record'
  | 'attio-list'
  | 'job-change'
  | 'new-people'
  | 'new-companies'
  | 'new-jobs'
  | 'post-engagers'
  | 'company-signal'
  | 'sheet-rows-added'
  | 'notion-page-added'
  | 'notion-page-updated'
  | 'google-doc-updated'
  | 'fireflies-call'
  | 'fathom-call'
  | 'gong-call'
  | 'webhook'
  | 'webhook-rb2b'
  | 'webhook-calendly-booked'
  | 'webhook-calendly-no-show'
  | 'webhook-segment'
  | 'list-leads-added'
  | 'tool-adopted'
  | 'sequence-event'
  | 'play-handoff';

export interface AppEvent {
  key: AppEventKey;
  label: string;
  line: string;
  kind: TriggerKind;
  crm?: PlayCrm;
  /** The webhook format a webhook row sets, as the builder's rows do. */
  preset?: WebhookPreset;
}

/** The app events a play's trigger card runs, as Gumloop's App Trigger lists them. */
export const APP_EVENTS: readonly AppEvent[] = [
  ...(['salesforce', 'hubspot', 'attio'] as const).flatMap((crm) =>
    (['deal_stage', 'deal_stalled'] as const).map(
      (kind): AppEvent => ({
        key: `${crm}-${kind.replace('_', '-')}` as AppEventKey,
        label: `${crmLabel(crm)} ${kind === 'deal_stage' ? 'deal changes stage' : 'deal stalls'}`,
        line:
          kind === 'deal_stage'
            ? 'A deal enters a chosen stage'
            : 'An open deal has no activity for the chosen days',
        kind,
        crm,
      })
    )
  ),
  {
    key: 'salesforce-sixsense-stage',
    label: 'An account reaches a 6sense stage',
    line: 'A Salesforce account reaches a 6sense stage',
    kind: 'sixsense_stage',
    crm: 'salesforce',
  },
  {
    key: 'company-signal',
    label: 'Account events',
    line: 'Hiring, news, funding and headcount at named accounts',
    kind: 'company_signal',
  },
  {
    key: 'sheet-rows-added',
    label: 'Rows are added to a Google Sheet',
    line: 'New rows in a tab you pick',
    kind: 'sheet_rows_added',
  },
  {
    key: 'notion-page-added',
    label: 'A page is added to a Notion database',
    line: 'New pages in a database you link',
    kind: 'notion_page_added',
  },
  {
    key: 'notion-page-updated',
    label: 'A page in a Notion database is updated',
    line: 'Edits to pages in a database you link',
    kind: 'notion_page_updated',
  },
  {
    key: 'google-doc-updated',
    label: 'A picked Google Doc is updated',
    line: 'Edits to Google Docs you pick',
    kind: 'google_doc_updated',
  },
  ...(['fireflies', 'fathom', 'gong'] as const).map(
    (tool): AppEvent => ({
      key: `${tool}-call`,
      label: `A ${{ fireflies: 'Fireflies', fathom: 'Fathom', gong: 'Gong' }[tool]} call is recorded`,
      line: 'Start with outside participants when call notes sync',
      kind: `${tool}_call`,
    })
  ),
  {
    key: 'webhook',
    label: 'A webhook arrives',
    line: 'Receive a form or JSON submission from another app',
    kind: 'webhook',
  },
  // The builder's webhook formats (`webhookSpec` in trigger-kinds), named
  // as its rows are.
  {
    key: 'webhook-rb2b',
    label: 'RB2B identifies a site visitor',
    line: 'Named visitors to your website',
    kind: 'webhook',
    preset: 'rb2b_visitor',
  },
  {
    key: 'webhook-calendly-booked',
    label: 'A Calendly meeting is booked',
    line: 'Invitees of a new Calendly booking',
    kind: 'webhook',
    preset: 'calendly_booked',
  },
  {
    key: 'webhook-calendly-no-show',
    label: 'A Calendly invitee is a no-show',
    line: 'Invitees who miss a Calendly meeting',
    kind: 'webhook',
    preset: 'calendly_no_show',
  },
  {
    key: 'webhook-segment',
    label: 'A Segment event arrives',
    line: 'A Segment Track call for a person',
    kind: 'webhook',
    preset: 'segment_track',
  },
  // Offered only where the play can save them: a sequence needs the
  // agent's play, a hand-off another play to send (`AppEventPicker`).
  {
    key: 'sequence-event',
    label: 'Someone replies or finishes a sequence',
    line: `Replies and finishes in a ${BRAND.name} sequence`,
    kind: 'sequence_event',
  },
  {
    key: 'play-handoff',
    label: 'Another play sends records',
    line: 'People or companies from another play',
    kind: 'play_handoff',
  },
  {
    key: 'meeting-booked',
    label: 'A Google Calendar meeting is booked',
    line: 'Follow up with outside attendees',
    kind: 'meeting_booked',
  },
  {
    key: 'meeting-starting',
    label: 'A Google Calendar meeting is starting',
    line: 'Prepare before a meeting with outside attendees',
    kind: 'meeting_starting',
  },
  {
    key: 'meeting-no-notes',
    label: 'A Google Calendar meeting has no notes',
    line: 'Check for missing notes two hours after a meeting',
    kind: 'meeting_no_notes',
  },
  {
    key: 'post-engagers',
    label: 'People engage with posts',
    line: 'Indexed engagers match your ICP',
    kind: 'post_engagers',
  },
  {
    key: 'new-jobs',
    label: 'Companies start hiring',
    line: 'New job posts match your filters',
    kind: 'new_jobs',
  },
  {
    key: 'salesforce-record',
    label: 'Salesforce record',
    line: 'A record matches your conditions',
    kind: 'crm_property_change',
    crm: 'salesforce',
  },
  {
    key: 'salesforce-list',
    label: 'Salesforce list',
    line: 'Someone joins a list view, report or campaign',
    kind: 'crm_list_membership',
    crm: 'salesforce',
  },
  // W5 CRM cleanup: offered for the CRM the cleanup seed picked
  // (`useCleanupSeed`).
  {
    key: 'salesforce-duplicates',
    label: 'Salesforce duplicates',
    line: 'Duplicate records, on a schedule',
    kind: 'crm_duplicates',
    crm: 'salesforce',
  },
  {
    key: 'hubspot-record',
    label: 'HubSpot record',
    line: 'A record matches your conditions',
    kind: 'crm_property_change',
    crm: 'hubspot',
  },
  {
    key: 'hubspot-list',
    label: 'HubSpot list',
    line: 'Someone joins a list',
    kind: 'crm_list_membership',
    crm: 'hubspot',
  },
  {
    key: 'hubspot-duplicates',
    label: 'HubSpot duplicates',
    line: 'Duplicate records, on a schedule',
    kind: 'crm_duplicates',
    crm: 'hubspot',
  },
  // Attio has no job change or duplicates rows: both read Salesforce or
  // HubSpot only (attio-build-plan D16).
  {
    key: 'attio-record',
    label: 'Attio record',
    line: 'A person or company matches your conditions',
    kind: 'crm_property_change',
    crm: 'attio',
  },
  {
    key: 'attio-list',
    label: 'Attio list',
    line: 'A person or company joins a list',
    kind: 'crm_list_membership',
    crm: 'attio',
  },
  {
    key: 'job-change',
    label: 'Job change',
    line: 'Someone at your CRM accounts changes jobs',
    kind: 'job_change',
  },
  {
    key: 'new-people',
    label: 'New people match',
    line: 'New people match an audience',
    kind: 'new_people',
  },
  {
    key: 'new-companies',
    label: 'New companies match',
    line: 'New companies match an audience',
    kind: 'new_companies',
  },
  {
    key: 'list-leads-added',
    label: 'Leads are added to a list',
    line: 'New leads on a list you pick',
    kind: 'list_leads_added',
  },
  {
    key: 'tool-adopted',
    label: 'Companies start using a tool',
    line: 'New companies using a technology who match your ICP',
    kind: 'tool_adopted',
  },
];

export function appEvent(key: string | undefined): AppEvent | undefined {
  return APP_EVENTS.find((item) => item.key === key);
}

/** The event a play trigger's config reads as. */
export function appEventOf(
  config: Partial<TriggerConfig> | undefined
): AppEvent | undefined {
  const kind = triggerKind(config);
  if (!kind) return undefined;
  if (
    kind === 'deal_stage' ||
    kind === 'deal_stalled' ||
    kind === 'crm_property_change' ||
    kind === 'crm_list_membership' ||
    kind === 'crm_duplicates'
  ) {
    // A missing crmType is a legacy Salesforce trigger.
    const crm = config?.crm?.crmType ?? 'salesforce';
    return APP_EVENTS.find((item) => item.kind === kind && item.crm === crm);
  }
  // A webhook reads as its format's row, else the plain webhook.
  if (kind === 'webhook') {
    const preset = config?.webhook?.preset;
    return (
      (preset && APP_EVENTS.find((item) => item.preset === preset)) ||
      APP_EVENTS.find((item) => item.kind === kind && !item.preset)
    );
  }
  return APP_EVENTS.find((item) => item.kind === kind);
}

/**
 * Whether the picker offers an app event: an Attio row only when the
 * organization's CRM options list Attio, as the Agent page offers its
 * connection (`offeredConnectionTypes`). Every other row is unchanged.
 */
export function appEventOffered(
  event: Pick<AppEvent, 'crm'>,
  crmOptions: readonly Pick<CrmOption, 'type'>[] | undefined
): boolean {
  return (
    event.crm !== 'attio' ||
    offeredConnectionTypes(crmOptions).includes('attio')
  );
}

/**
 * A new app trigger's starting config: a CRM row on its own CRM, any other
 * on the first connected CRM (`triggerConfigForKind`). An Attio deal row
 * starts on Attio with no pipeline, since Attio deal stages have none.
 */
export function appEventConfig(
  event: Pick<AppEvent, 'kind' | 'crm' | 'preset'>,
  connectedCrmTypes: readonly string[]
): TriggerConfig {
  return triggerConfigForKind(
    event.kind,
    event.crm ? [event.crm] : connectedCrmTypes,
    undefined,
    undefined,
    event.preset
  );
}

/**
 * The form a saved play trigger opens in: its app event, else one named for
 * its kind, so a kind the picker doesn't list (Companies start using a tool,
 * a lead list, a webhook) still opens. A schedule opens the Schedule form.
 */
export function savedEventOf(
  config: Partial<TriggerConfig> | undefined
): AppEvent | undefined {
  const event = appEventOf(config);
  if (event) return event;
  const kind = triggerKind(config);
  // A brief's saved kind is schedule or one_time, outside TriggerKind.
  if (!kind || ['schedule', 'one_time'].includes(kind as string))
    return undefined;
  // Not a picker row, so its key routes nowhere; the form reads only kind.
  return {
    key: kind as unknown as AppEventKey,
    label: triggerLabel(kind),
    line: '',
    kind,
  };
}

/** An audience trigger: its audience is edited on the Play tab's trigger card. */
export function isAudienceKind(kind: TriggerKind | null | undefined): boolean {
  return kind === 'new_people' || kind === 'new_companies';
}

/**
 * The agent's triggers once they have a kind: each of its plays' (a play has
 * one), else its own. What the Triggers tab counts.
 */
export function setTriggers(agent: Pick<AgentDto, 'triggers'>): TriggerDto[] {
  return agent.triggers.filter((trigger) => trigger.kind);
}

/** The agent's first trigger once it has a kind. */
export function setTrigger(
  agent: Pick<AgentDto, 'triggers'>
): TriggerDto | undefined {
  return setTriggers(agent)[0];
}

export type KindOption = 'clu' | 'schedule' | 'app';

/**
 * The kinds the list offers: Clu, a schedule (a scheduled brief) and app
 * triggers (an app trigger gives the agent a play).
 */
export const KIND_OPTIONS: readonly KindOption[] = ['clu', 'schedule', 'app'];

/** An agent's own schedule or one-time trigger: stored, never run. */
export function isAgentTrigger(trigger: Pick<TriggerDto, 'source'>): boolean {
  return trigger.source === 'agent';
}

/** What an agent's own trigger shows in place of its next run. */
export const NOT_RUNNING = 'Not running';

function nameOf(trigger: Pick<TriggerDto, 'config'>): string {
  const name = (trigger.config as { name?: unknown }).name;
  return typeof name === 'string' ? name.trim() : '';
}

/** A trigger in words: its name, else what starts it. */
export function triggerTitle(
  trigger: Pick<TriggerDto, 'kind' | 'config' | 'schedule'>
): string {
  const name = nameOf(trigger);
  if (name) return name;
  if (trigger.kind === 'schedule') return scheduleWithZone(trigger.schedule);
  if (trigger.kind === 'one_time')
    return oneTimeWords({
      ...(trigger.config as OneTimeConfig),
      ...((trigger.schedule ?? {}) as OneTimeConfig),
    });
  if (trigger.kind)
    return appEventOf(trigger.config)?.label ?? triggerLabel(trigger.kind);
  return triggerLabel(trigger.kind);
}

/**
 * A play trigger's health worth showing in place of its line: the poll's
 * pause, a failing check or a long live run (`triggerHealthNotice`).
 */
export function triggerNotice(
  trigger: Pick<TriggerDto, 'source' | 'enabled' | 'health'>
): TriggerHealthNotice | null {
  if (isAgentTrigger(trigger)) return null;
  return triggerHealthNotice(trigger.health, trigger.enabled);
}

/**
 * The muted line under a trigger: Not running for an agent's own trigger,
 * else its health notice, else the next or last fire, else what it watches.
 */
export function triggerLine(
  trigger: Pick<
    TriggerDto,
    | 'kind'
    | 'config'
    | 'schedule'
    | 'nextAt'
    | 'lastFiredAt'
    | 'source'
    | 'enabled'
    | 'health'
  >
): string {
  if (isAgentTrigger(trigger)) return NOT_RUNNING;
  const notice = triggerNotice(trigger);
  if (notice) return notice.text;
  if (trigger.nextAt) return 'Next run ' + timeAgo(trigger.nextAt);
  if (trigger.lastFiredAt) return 'Last ran ' + timeAgo(trigger.lastFiredAt);
  return (
    nodeSummary('trigger', {
      ...(trigger.config as Partial<TriggerConfig>),
      schedule: trigger.schedule ?? undefined,
    } as TriggerConfig) ?? ''
  );
}

/**
 * The play's trigger as the PATCH writes it: the card's config as the
 * builder saves it (`withDerivedTriggerWire`: a search's Crustdata wire from
 * its filters, a job change's saved shape), its name kept in it, the
 * schedule apart, the prompt as the note. The server runs the wire as sent.
 */
export function playTriggerEntry({
  id,
  config,
  name,
  note,
  enabled,
}: {
  id: string;
  config: Partial<TriggerConfig>;
  name: string;
  note: string;
  enabled: boolean;
}): TriggerInput {
  const graph: CustomGraph = {
    version: 1,
    nodes: [
      {
        id,
        type: 'trigger',
        position: { x: 0, y: 0 },
        config: config as TriggerConfig,
      },
    ],
    edges: [],
  };
  const current = withDerivedTriggerWire(graph, true).nodes[0]
    .config as Partial<TriggerConfig>;
  const { schedule, ...rest } = current;
  const withName: Record<string, unknown> = { ...rest };
  if (name.trim()) withName.name = name.trim();
  else delete withName.name;
  return {
    id,
    kind: triggerKind(config) ?? null,
    config: withName,
    schedule: schedule ?? null,
    note: note.trim() ? note : null,
    enabled,
    source: 'play',
  };
}

/** A play's trigger card set back to unset: no kind, schedule or prompt. */
export function clearedPlayTrigger(
  trigger: Pick<TriggerDto, 'id' | 'enabled'>
): TriggerInput {
  return {
    id: trigger.id,
    kind: null,
    config: {},
    schedule: null,
    note: null,
    enabled: trigger.enabled,
    source: 'play',
  };
}
